import { Box, FormControl, IconButton, InputLabel, MenuItem, Paper, Select, Stack, Typography } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import AddIcon from "@mui/icons-material/Add";
import { useState } from "react";
import { v4 as uuidv4 } from "uuid";

interface Props {
    block: GridBlock;
    onChange: (content: any) => void;
    onDelete: () => void;
}

const GridBlockEditor = ({ block, onChange, onDelete }: Props) => {
    const [columns, setColumns] = useState(block.content.columns || 2);

    const handleColumnsChange = (newColumns: number) => {
        setColumns(newColumns);
        const newBlocks: PageBlock[][] = [];
        
        // Initialize with empty arrays for each column
        for (let i = 0; i < newColumns; i++) {
            newBlocks[i] = block.content.blocks[i] || [];
        }
        
        onChange({
            columns: newColumns,
            blocks: newBlocks,
        });
    };

    const addBlockToColumn = (columnIndex: number) => {
        const newBlocks = [...block.content.blocks];
        if (!newBlocks[columnIndex]) {
            newBlocks[columnIndex] = [];
        }
        
        newBlocks[columnIndex].push({
            id: uuidv4(),
            type: PageBlockType.TEXT,
            order: newBlocks[columnIndex].length,
            content: { html: "<p>Enter text here...</p>" },
        });
        
        onChange({
            ...block.content,
            blocks: newBlocks,
        });
    };

    const removeBlockFromColumn = (columnIndex: number, blockIndex: number) => {
        const newBlocks = [...block.content.blocks];
        newBlocks[columnIndex].splice(blockIndex, 1);
        
        onChange({
            ...block.content,
            blocks: newBlocks,
        });
    };

    return (
        <Paper sx={{ p: 2, mb: 2 }}>
            <Stack direction="row" spacing={1} alignItems="flex-start">
                <IconButton size="small" sx={{ cursor: "grab" }}>
                    <DragIndicatorIcon />
                </IconButton>
                <Box flex={1}>
                    <Stack spacing={2}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <Typography variant="h6">Grid Layout</Typography>
                            <FormControl size="small" sx={{ minWidth: 120 }}>
                                <InputLabel>Columns</InputLabel>
                                <Select
                                    value={columns}
                                    label="Columns"
                                    onChange={(e) => handleColumnsChange(Number(e.target.value))}
                                >
                                    <MenuItem value={1}>1 Column</MenuItem>
                                    <MenuItem value={2}>2 Columns</MenuItem>
                                    <MenuItem value={3}>3 Columns</MenuItem>
                                    <MenuItem value={4}>4 Columns</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>
                        
                        <Box sx={{ display: "grid", gridTemplateColumns: `repeat(${columns}, 1fr)`, gap: 2 }}>
                            {Array.from({ length: columns }).map((_, columnIndex) => (
                                <Paper
                                    key={columnIndex}
                                    variant="outlined"
                                    sx={{ p: 2, minHeight: 100, bgcolor: "#f9f9f9" }}
                                >
                                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                        Column {columnIndex + 1}
                                    </Typography>
                                    
                                    <Stack spacing={1}>
                                        {block.content.blocks[columnIndex]?.map((nestedBlock, blockIndex) => (
                                            <Paper key={nestedBlock.id} sx={{ p: 1, bgcolor: "white" }}>
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <Typography variant="body2" sx={{ flex: 1 }}>
                                                        {nestedBlock.type} block
                                                    </Typography>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => removeBlockFromColumn(columnIndex, blockIndex)}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Stack>
                                            </Paper>
                                        ))}
                                        
                                        <IconButton
                                            size="small"
                                            onClick={() => addBlockToColumn(columnIndex)}
                                            sx={{ alignSelf: "center" }}
                                        >
                                            <AddIcon />
                                        </IconButton>
                                    </Stack>
                                </Paper>
                            ))}
                        </Box>
                        
                        <Typography variant="caption" color="text.secondary">
                            Note: Nested blocks will use simplified rendering. For complex layouts, consider using multiple grid blocks.
                        </Typography>
                    </Stack>
                </Box>
                <IconButton size="small" onClick={onDelete} color="error">
                    <DeleteIcon />
                </IconButton>
            </Stack>
        </Paper>
    );
};

export default GridBlockEditor;
